// @flow
import * as React from "react";
import idx from "idx";
import memoize from "lodash/memoize";
import querystring from "querystring";
import { apiCall, reportServerFault } from "../common/fetch";
import Optimistic from "./Optimistic";
import { DetermineErrorPopup } from "./Popups/Error";
import { inferHelpdeskLocale } from "../Helpdesk/withFetch";

import type { Profile } from "../../redux/CommonTypes";

const Tournament = React.createContext(null);
const OptimisticTournamentContext = React.createContext(null);

export const ActiveTournament = OptimisticTournamentContext.Consumer;

export default Tournament;

const INVALIDATION_THRESHOLD = 60 * 1000;

type Props = {
  onLogout: () => void,
  profile?: Profile,
  currency: string,
  locale: string,
  children: React.Node
};

type State = {
  errorCode: null | string
};

type Options = {| [key: string]: string |} | {||};

function validateSensibleResponse(response, requestTime) {
  switch (response.status) {
    case "OK":
      return {
        status: "Resolved",
        lastRequestTime: requestTime,
        value: response.payload
      };
    case "ERROR":
      return {
        status: "Rejected",
        lastRequestTime: requestTime,
        code: response.suspicious ? "suspicious" : response.code,
        message: response.message,
        error: null
      };
    default:
      throw new TypeError("Unexpected response status");
  }
}

let insaneResponseValidations = {
  "/api/withdraw/banks/branches": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/ajax/declineBonus": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/request-withdraw": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/announcements": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/bell-tolls-items": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/ajax/stockHistory": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/profile": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/getAssets": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/promo-code-info": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/getAccounts": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/is-restricted": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  },
  "/api/withdraw/output-methods": (response, requestTime) => {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  }
};

function validateResponse(key: string, response: any, requestTime: number) {
  let method = key.split("?")[0];

  if (key.startsWith("/helpdesk")) {
    return {
      status: "Resolved",
      lastRequestTime: requestTime,
      value: response
    };
  } else if (insaneResponseValidations[method] != null) {
    return insaneResponseValidations[method](response, requestTime);
  } else {
    return validateSensibleResponse(response, requestTime);
  }
}

type FetchOptions = {
  ...Options,
  request?: Options,
  mode?: "helpdesk"
};

export class TournamentAPIProvider extends React.Component<Props, State> {
  state = {
    errorCode: null,
    lastUpdatedEntity: null
  };

  cache = {};
  timer: IntervalID = null;

  setCache(updater) {
    let diff = updater(this.cache);
    Object.assign(this.cache, diff);
  }

  optimisticTournamentRef = React.createRef();

  helpdeskQueries = (key: string) => {
    type QueriesObj = {
      lang: string,
      currency: string,
      tags?: number
    };

    let additionalQueries = {};

    if (key.includes("?")) {
      additionalQueries = querystring.parse(key.split("?")[1]);
      key = key.split("?")[0];
    }

    const object: QueriesObj = {
      ...additionalQueries,
      lang: inferHelpdeskLocale(this.props.locale),
      currency: this.props.currency.toUpperCase()
    };

    if (this.props.profile != null) {
      object.tags = 4;
    }

    return `${key}?${querystring.stringify(object)}`;
  };

  getEntity = (key: string) => {
    if (key.startsWith("/helpdesk")) {
      key = this.helpdeskQueries(key);
    }

    let entity = this.cache[key];

    if (entity == null) {
      let freshEntity = { status: "Empty", value: null };
      this.cache[key] = freshEntity;
      return freshEntity;
    } else {
      return entity;
    }
  };

  actuallyFetchEntity = (
    key: string,
    options: FetchOptions = { request: {} }
  ) => {
    let time = Date.now();
    let { request } = options;

    if (key.startsWith("/helpdesk")) {
      key = this.helpdeskQueries(key);
      request = {
        headers: {
          "X-LOCALE": inferHelpdeskLocale(this.props.locale),
          "X-CURRENCY": this.props.currency.toUpperCase(),
          accept: "application/json"
        }
      };
    }

    let nextVersionPromise = apiCall(key, request)
      .then(async response => {
        if (response.status === 401) {
          this.props.onLogout();
          console.warn("Authentication needed");
        }

        if (response.status >= 500) {
          throw new TypeError("Server error, status " + response.status);
        }

        try {
          let body = await response.json();

          if (!key.startsWith("/helpdesk") && body.status !== "OK") {
            reportServerFault({
              endpoint: key,
              status: response.status,
              statusText: response.statusText,
              options: {},
              message: body.message,
              response: body
            });
          }

          if (
            key.startsWith("/helpdesk") &&
            response.headers.get("x-total-pages") != null
          ) {
            return {
              pages: response.headers.get("x-total-pages"),
              data: body
            };
          }

          return body;
        } catch (error) {
          reportServerFault({
            endpoint: key,
            status: response.status,
            statusText: response.statusText,
            options: {},
            message: "Unable to parse response"
          });

          throw error;
        }
      })
      .then(
        body => validateResponse(key, body, time),
        error => {
          return {
            status: "Rejected",
            lastRequestTime: time,
            error: error
          };
        }
      )
      .catch(console.info);

    this.setCache(cache => {
      let prevVerison = cache[key];

      let nextVersion = {
        status: "Pending",
        lastRequestTime: time,
        promise: nextVersionPromise,
        value: prevVerison != null ? prevVerison.value : null
      };

      return {
        [key]: nextVersion
      };
    });

    nextVersionPromise.then(nextVersion => {
      this.setCache(cache => {
        let prevVerison = cache[key];

        if (prevVerison != null) {
          return {
            [key]: {
              value: prevVerison.value,
              ...nextVersion
            }
          };
        }

        return {
          [key]: nextVersion
        };
      });
      this.setState(state => {
        return {
          lastUpdatedEntity: nextVersion,
          errorCode:
            nextVersion.status === "Rejected"
              ? nextVersion.code
              : state.errorCode
        };
      });
    });

    return nextVersionPromise;
  };

  invalidateEntity = (key: string, options: Options = {}) => {
    let entity = this.getEntity(key);

    if (entity.status === "Pending" && options.force !== true) {
      return entity.promise;
    }

    let promise = this.actuallyFetchEntity(key, options);

    // Кастомная логика для инвалидациии /stats,
    // потому что там хранится баланс, а за его
    // состоянием мы следим особенно пристально.
    if (
      key === "/api/tournaments/stats" &&
      this.optimisticTournamentRef.current != null
    ) {
      this.optimisticTournamentRef.current.invalidate({ promise });
    }

    return promise;
  };

  handleStatsInvalidation = (options: Options) => {
    let promise =
      options != null && options.promise != null
        ? options.promise
        : this.invalidateEntity("/api/tournaments/stats");

    return promise.then(entity => {
      if (entity.status === "Resolved") {
        return entity.value.active_tournament;
      } else {
        throw entity.error;
      }
    });
  };

  readEntity = (key: string, options: Options = {}) => {
    let entity = this.getEntity(key);

    if (entity.status === "Empty") {
      this.actuallyFetchEntity(key, options);
      return this.getEntity(key);
    } else if (
      entity.status === "Resolved" &&
      typeof options.follow === "string"
    ) {
      // Фича follow позволяет перезапросить данные если какой-то
      // другой метод обновился недавно
      let followedEntity = this.getEntity(options.follow);

      if (
        typeof entity.lastRequestTime === "number" &&
        typeof followedEntity.lastRequestTime === "number" &&
        entity.lastRequestTime < followedEntity.lastRequestTime
      ) {
        this.actuallyFetchEntity(key, options);
        return this.getEntity(key);
      } else {
        return entity;
      }
    } else {
      return entity;
    }
  };

  clearEntities = (startKey: string) => {
    for (let key in this.cache) {
      if (this.cache.hasOwnProperty(key) && key.startsWith(startKey)) {
        this.setCache(cache => {
          let prevVersion = cache[key];

          let nextVersion = {
            status: "Empty",
            value: prevVersion != null ? prevVersion.value : null
          };

          return {
            [key]: nextVersion
          };
        });
      }
    }
  };

  createRef = memoize((kek, errorCode) => ({
    errorCode: errorCode,
    dismissError: this.dismissError,
    simulateError: this.simulateError,
    // getEntity: this.getEntity,
    readEntity: this.readEntity,
    fetchEntity: this.invalidateEntity,
    clearEntities: this.clearEntities
  }));

  invalidationLoop = () => {
    let importantEntities =
      this.props.profile != null ? ["/api/tournaments/stats"] : [];

    for (let key of importantEntities) {
      let entity = this.getEntity(key);

      switch (entity.status) {
        case "Empty":
          this.invalidateEntity(key);
          break;
        case "Pending":
          break;
        case "Resolved":
        case "Rejected":
          if (
            Date.now() - entity.lastRequestTime + 1000 >
            INVALIDATION_THRESHOLD
          ) {
            this.invalidateEntity(key);
          }
          break;
        default:
          break;
      }
    }
  };

  dismissError = () => {
    this.setState({
      errorCode: null
    });
  };

  simulateError = (code: string) => {
    this.setState({
      errorCode: code
    });
  };

  componentDidMount() {
    this.timer = setInterval(this.invalidationLoop, INVALIDATION_THRESHOLD);
  }

  componentDidUpdate(prevProps) {
    if (this.props.locale !== prevProps.locale) {
      for (let key in this.cache) {
        if (this.cache.hasOwnProperty(key)) {
          if (
            key.startsWith("/api/tournaments/stats") ||
            key.startsWith("/api/tournaments/info") ||
            key.startsWith("/api/tournaments/list") ||
            key.startsWith("/api/announcements") ||
            key.startsWith("/api/bell-tolls-items")
          ) {
            this.invalidateEntity(key);
          }
        }
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  render() {
    let active_tournament = null;

    if (this.props.profile != null) {
      active_tournament = idx(
        this.readEntity("/api/tournaments/stats"),
        stats => stats.value.active_tournament
      );
    }

    return (
      <Tournament.Provider
        value={this.createRef(
          this.state.lastUpdatedEntity,
          this.state.errorCode
        )}
      >
        <Optimistic
          ref={this.optimisticTournamentRef}
          value={active_tournament}
          onInvalidate={this.handleStatsInvalidation}
        >
          {tournament => (
            <OptimisticTournamentContext.Provider value={tournament}>
              <DetermineErrorPopup errorCode={this.state.errorCode} />
              {this.props.children}
            </OptimisticTournamentContext.Provider>
          )}
        </Optimistic>
      </Tournament.Provider>
    );
  }
}

export type ApiProps = {|
  dismissError: () => void,
  errorCode: string | null,
  fetchEntity: (
    key: string,
    options?: Options
  ) =>
    | any
    | Promise<
        | {|
            lastRequestTime: number,
            status: string,
            value: any
          |}
        | {|
            error: any,
            lastRequestTime: number,
            status: string
          |}
        | {|
            code: any,
            error: null,
            lastRequestTime: number,
            message: any,
            status: string
          |}
      >,
  readEntity: Function,
  clearEntities: (key: string) => mixed,
  simulateError: (code: string) => void
|};



// WEBPACK FOOTER //
// ./src/components/Tournaments/Api.js